/* ProgressiveProfile — small, single-section profile input flow used by the
 * fallback funnel. User arrives via ProfileCompletenessCard next-best action
 * or from the paywall "Continue free" path. Each field auto-saves on blur.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, ChevronRight, ChevronLeft, Ruler, Scale, Activity, Loader2, Sparkles } from "lucide-react";

const SECTIONS = [
  {
    id: "body",
    icon: Ruler,
    title_bg: "Ръст и тегло",
    title_en: "Height & weight",
    fields: [
      { id: "height", label_bg: "Ръст (см)", label_en: "Height (cm)", type: "number", min: 100, max: 250, step: 1 },
      { id: "weight", label_bg: "Тегло (кг)", label_en: "Weight (kg)", type: "number", min: 30, max: 250, step: 0.1 },
    ],
  },
  {
    id: "measurements",
    icon: Scale,
    title_bg: "Обиколки",
    title_en: "Body measurements",
    fields: [
      { id: "waist", label_bg: "Талия (см)", label_en: "Waist (cm)", type: "number", min: 40, max: 200, step: 0.5 },
      { id: "hip", label_bg: "Ханш (см)", label_en: "Hip (cm)", type: "number", min: 50, max: 200, step: 0.5 },
      { id: "neck", label_bg: "Шия (см)", label_en: "Neck (cm)", type: "number", min: 20, max: 80, step: 0.5 },
    ],
  },
  {
    id: "lifestyle",
    icon: Activity,
    title_bg: "Начин на живот",
    title_en: "Lifestyle",
    fields: [
      {
        id: "activityLevel", label_bg: "Физическа активност", label_en: "Activity level", type: "select",
        options: [
          { value: "sedentary", label_bg: "Заседнал", label_en: "Sedentary" },
          { value: "light", label_bg: "Лек", label_en: "Light" },
          { value: "moderate", label_bg: "Умерен", label_en: "Moderate" },
          { value: "active", label_bg: "Активен", label_en: "Active" },
          { value: "very_active", label_bg: "Много активен", label_en: "Very active" },
        ],
      },
      {
        id: "sleepHours", label_bg: "Сън (часове)", label_en: "Sleep (hours)", type: "select",
        options: [
          { value: "<5", label_bg: "<5 часа", label_en: "<5 hours" },
          { value: "5-6", label_bg: "5–6 часа", label_en: "5–6 hours" },
          { value: "7", label_bg: "7 часа", label_en: "7 hours" },
          { value: "8", label_bg: "8 часа", label_en: "8 hours" },
          { value: "9+", label_bg: "9+ часа", label_en: "9+ hours" },
        ],
      },
      {
        id: "stressLevel", label_bg: "Ниво на стрес", label_en: "Stress level", type: "select",
        options: [
          { value: "low", label_bg: "Нисък", label_en: "Low" },
          { value: "moderate", label_bg: "Умерен", label_en: "Moderate" },
          { value: "high", label_bg: "Висок", label_en: "High" },
        ],
      },
      {
        id: "dietType", label_bg: "Тип хранене", label_en: "Diet type", type: "select",
        options: [
          { value: "mixed", label_bg: "Смесено", label_en: "Mixed" },
          { value: "vegetarian", label_bg: "Вегетарианско", label_en: "Vegetarian" },
          { value: "vegan", label_bg: "Веган", label_en: "Vegan" },
          { value: "keto", label_bg: "Кето", label_en: "Keto" },
          { value: "mediterranean", label_bg: "Средиземноморско", label_en: "Mediterranean" },
        ],
      },
    ],
  },
];

export default function ProgressiveProfile() {
  const { API, user, refreshUser } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const bg = language === "bg";

  // Start on the section from ?section= or the first incomplete section
  const requestedSection = params.get("section");
  const initialIdx = Math.max(0, SECTIONS.findIndex(s => s.id === requestedSection));
  const [sectionIdx, setSectionIdx] = useState(initialIdx === -1 ? 0 : initialIdx);

  const [values, setValues] = useState({});
  const [savingField, setSavingField] = useState(null);
  const [completeness, setCompleteness] = useState(null);

  // Prefill from user profile
  useEffect(() => {
    if (!user?.profile) return;
    const p = user.profile;
    setValues({
      height: p.body?.height || "",
      weight: p.body?.weight || "",
      waist: p.measurements?.waist || "",
      hip: p.measurements?.hip || "",
      neck: p.measurements?.neck || "",
      activityLevel: p.lifestyle?.activityLevel || "",
      sleepHours: p.lifestyle?.sleepHours || "",
      stressLevel: p.lifestyle?.stressLevel || "",
      dietType: p.lifestyle?.dietType || "",
    });
  }, [user]);

  const refreshCompleteness = () => {
    axios.get(`${API}/user/profile-completeness`)
      .then(({ data }) => setCompleteness(data))
      .catch(() => {});
  };
  useEffect(() => { refreshCompleteness(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (id, v) => setValues(prev => ({ ...prev, [id]: v }));

  const saveField = async (id) => {
    const v = values[id];
    if (v === "" || v === undefined || v === null) return;
    setSavingField(id);
    try {
      await axios.patch(`${API}/user/profile/progressive`, { [id]: v });
      await refreshUser();
      refreshCompleteness();
    } catch (e) {
      toast.error(bg ? "Грешка при запис" : "Save error");
    } finally {
      setSavingField(null);
    }
  };

  const section = SECTIONS[sectionIdx];
  const Icon = section.icon;
  const isLast = sectionIdx === SECTIONS.length - 1;
  const progress = ((sectionIdx + 1) / SECTIONS.length) * 100;

  return (
    <div className={`min-h-screen py-6 sm:py-10 px-4 ${isDark ? "bg-background" : "bg-gradient-to-br from-[#F0F9FF] via-[#FAFBFC] to-[#F0FDF4]"}`}>
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0EA5E9] to-[#6366F1] mb-3">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <h1 className={`text-xl sm:text-2xl font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? "Допълнете профила си" : "Complete your profile"}
          </h1>
          <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            {bg ? "Всеки ред се записва автоматично." : "Each row is saved automatically."}
          </p>
        </div>

        {/* Section stepper */}
        <div className="mb-5">
          <div className="flex justify-between mb-2">
            {SECTIONS.map((s, i) => {
              const SIcon = s.icon;
              const active = i === sectionIdx;
              const done = i < sectionIdx;
              return (
                <button
                  key={s.id}
                  onClick={() => setSectionIdx(i)}
                  className="flex flex-col items-center flex-1"
                  data-testid={`progressive-step-${s.id}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-1 ${
                    done ? "bg-emerald-500 text-white"
                    : active ? "bg-[#0EA5E9] text-white shadow-md"
                    : isDark ? "bg-[hsl(223,47%,18%)] text-gray-500" : "bg-gray-100 text-gray-400"
                  }`}>
                    {done ? <CheckCircle2 className="w-4 h-4" /> : <SIcon className="w-4 h-4" />}
                  </div>
                  <span className={`text-[10px] hidden sm:block ${active ? "text-[#0EA5E9] font-medium" : isDark ? "text-gray-500" : "text-gray-400"}`}>
                    {bg ? s.title_bg : s.title_en}
                  </span>
                </button>
              );
            })}
          </div>
          <Progress value={progress} className="h-1.5" />
          {completeness && (
            <p className={`text-[11px] mt-2 text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {bg ? `Точност на профила: ${completeness.percent}%` : `Profile accuracy: ${completeness.percent}%`}
            </p>
          )}
        </div>

        <Card className={isDark ? "bg-card border-border" : "card-elevated border-0"}>
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br from-sky-500 to-indigo-500">
                <Icon className="w-4 h-4 text-white" />
              </div>
              <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {bg ? section.title_bg : section.title_en}
              </h2>
            </div>

            <div className="space-y-4">
              {section.fields.map(f => (
                <div key={f.id} data-testid={`progressive-field-${f.id}`}>
                  <label className={`text-xs font-medium mb-1.5 block ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    {bg ? f.label_bg : f.label_en}
                    {savingField === f.id && <Loader2 className="inline w-3 h-3 ml-1 animate-spin text-[#0EA5E9]" />}
                  </label>
                  {f.type === "select" ? (
                    <select
                      value={values[f.id] ?? ""}
                      onChange={(e) => handleChange(f.id, e.target.value)}
                      onBlur={() => saveField(f.id)}
                      className={`w-full rounded-lg px-3 py-2 text-sm border focus:ring-1 focus:ring-[#0EA5E9] focus:border-[#0EA5E9] ${
                        isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,20%)] text-gray-200" : "bg-white border-gray-200"
                      }`}
                      data-testid={`progressive-input-${f.id}`}
                    >
                      <option value="">{bg ? "Изберете…" : "Select…"}</option>
                      {f.options.map(o => (
                        <option key={o.value} value={o.value}>{bg ? o.label_bg : o.label_en}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={f.type}
                      min={f.min}
                      max={f.max}
                      step={f.step}
                      value={values[f.id] ?? ""}
                      onChange={(e) => handleChange(f.id, e.target.value)}
                      onBlur={() => saveField(f.id)}
                      className={`w-full rounded-lg px-3 py-2 text-sm border focus:ring-1 focus:ring-[#0EA5E9] focus:border-[#0EA5E9] ${
                        isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,20%)] text-gray-200" : "bg-white border-gray-200"
                      }`}
                      data-testid={`progressive-input-${f.id}`}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100 dark:border-[hsl(223,47%,18%)]">
              <Button
                variant="ghost"
                onClick={() => setSectionIdx(i => Math.max(0, i - 1))}
                disabled={sectionIdx === 0}
                data-testid="progressive-back-btn"
                className="h-10"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> {bg ? "Назад" : "Back"}
              </Button>
              {isLast ? (
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="gradient-primary text-white h-10 rounded-xl"
                  data-testid="progressive-finish-btn"
                >
                  {bg ? "Към таблото" : "Go to dashboard"} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={() => setSectionIdx(i => i + 1)}
                  className="gradient-primary text-white h-10 rounded-xl"
                  data-testid="progressive-next-btn"
                >
                  {bg ? "Напред" : "Next"} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-5">
          <button
            onClick={() => navigate("/dashboard")}
            className={`text-xs ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
            data-testid="progressive-skip-btn"
          >
            {bg ? "Прескочи за сега — ще довършиш по-късно" : "Skip for now — finish later"}
          </button>
        </div>
      </div>
    </div>
  );
}
